import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { documents as documentsApi, API_BASE_URL } from '../utils/api';
import { formatDate } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import { ArrowDownTrayIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import DocumentEditForm from '../components/Documents/DocumentEditForm';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { Link } from 'react-router-dom';

const DocumentView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const doc = await documentsApi.getById(id);
        setDocumentData(doc);
      } catch (err) {
        setError(err.message || 'Failed to load document');
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [id]);

  const handleDownload = async () => {
    try {
      const blob = await documentsApi.download(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = documentData.title;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download document:', error);
    }
  };

  const [deleteError, setDeleteError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDelete = async () => {
    try {
      setDeleteError(null);
      await documentsApi.delete(id);
      navigate('/documents', { state: { refresh: true } });
    } catch (error) {
      setDeleteError(error.message || 'Failed to delete document');
      // If it's a 404, the document doesn't exist anymore
      if (error.response?.status === 404) {
        navigate('/documents', { state: { refresh: true } });
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error || deleteError) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-red-600">{deleteError || error}</div>
      </div>
    );
  }

  if (!documentData) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-gray-600">Document not found</div>
      </div>
    );
  }

  const isPDF = documentData.file_type === 'application/pdf';
  const isImage = documentData.file_type?.startsWith('image/');

  return (
    <div className="container mx-auto p-2 sm:p-4 h-full flex flex-col">
      <div className="bg-white shadow sm:rounded-lg flex-1 flex flex-col min-h-0">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 sm:px-6 flex-shrink-0">
          <div className="flex flex-wrap justify-between items-center gap-2">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {documentData.title}
            </h3>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowEditModal(true)}
                className="inline-flex items-center p-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <PencilIcon className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">Edit</span>
              </button>
              <button
                onClick={handleDownload}
                className="inline-flex items-center p-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">Download</span>
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="inline-flex items-center p-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <TrashIcon className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">Delete</span>
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 py-5 sm:p-6 flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
            {/* Preview */}
            <div className="space-y-4 flex flex-col">
              <h4 className="text-sm font-medium text-gray-900 flex-shrink-0">Preview</h4>
              <div className="border rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center flex-1" style={{ minHeight: '400px' }}>
                {isPDF && (
                  <iframe
                    src={`${API_BASE_URL}/documents/${id}/preview`}
                    className="w-full h-full border-0"
                    title="PDF preview"
                  >
                    <div className="text-gray-500">
                      PDF preview not available. Please download to view.
                    </div>
                  </iframe>
                )}
                {isImage && (
                  <img
                    src={`${API_BASE_URL}/documents/${id}/preview`}
                    alt={documentData.title}
                    className="max-w-full max-h-full object-contain"
                  />
                )}
                {!isPDF && !isImage && (
                  <div className="text-gray-500">Preview not available</div>
                )}
              </div>
            </div>

            {/* Details */}
            <div className="space-y-6 lg:overflow-y-auto">
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-4">Document Details</h4>
                <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Type</dt>
                    <dd className="mt-1 text-sm text-gray-900">{documentData.type_name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Created</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatDate(documentData.created_at, { user, includeTime: true })}
                    </dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Notes</dt>
                    <dd className="mt-1 text-sm text-gray-900">{documentData.notes || '-'}</dd>
                  </div>
                </dl>
              </div>

              {/* Associated Coins */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-4">Associated Coins</h4>
                {documentData.coins?.length > 0 ? (
                  <ul className="divide-y divide-gray-200 border-t border-b border-gray-200">
                    {documentData.coins.map((coin) => (
                      <li key={coin.id} className="py-3">
                        <Link
                          to={`/coins/${coin.id}`}
                          className="block hover:bg-gray-50 transition-colors duration-150 -mx-2 px-2"
                        >
                          <div className="text-sm font-medium text-indigo-600">{coin.user_coin_id}</div>
                          <div className="text-sm text-gray-900">
                            {[
                              coin.country,
                              coin.mint_mark ? `${coin.year}-${coin.mint_mark}` : coin.year,
                              coin.denomination,
                              coin.variety
                            ].filter(Boolean).join(' ')}
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-sm text-gray-500">No coins associated with this document</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <DocumentEditForm
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={(updatedDoc) => {
          // Fetch fresh data to ensure we have all relationships
          const fetchUpdatedDocument = async () => {
            try {
              const doc = await documentsApi.getById(id);
              setDocumentData(doc);
            } catch (err) {
              setError(err.message || 'Failed to load document');
            }
          };
          fetchUpdatedDocument();
          setShowEditModal(false);
        }}
        document={documentData}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Document"
        message={`Are you sure you want to delete "${documentData?.title}"? This action cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
};

export default DocumentView;
